// In the name of Allah, the Greatest and Most Merciful. Praise be to Allah, Lord of the Worlds.
// NexoraX NexoraX QR Generator
// Feature: SecurePayment-Linked QR Code generation for client invoices with rapid 5% deposit settlement,
// Tiered Extraction Protocol (1% 3% Community Allocation split, 10% Maintenance & Hosting Fee) and Google Drive backup mirroring.
// Intellectual Property Status: Patent-Readiness Protected Assets, © NexoraX Digital Ltd (SC887095)

import React, { useState, useEffect, useRef } from 'react';
import { 
  QrCode, Landmark, ShieldCheck, Download, Copy, Check, RefreshCw, 
  Database, FileText, Send, HelpCircle, HardDrive, Sparkles, Building,
  ArrowRight, HeartHandshake, AlertCircle, TrendingUp, Key, Lock, CheckCircle
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';

interface InvoiceData {
  invoiceId: string;
  clientName: string;
  clientEmail: string;
  grossAmount: number;
  description: string;
  issueDate: string;
}

export const NexoraXQRGenerator: React.FC = () => {
  // Configured Secure Payment Provider Anchor Details
  const bankDetails = {
    bankName: "Secure Payment Provider",
    sortCode: "Verified Payment Configuration",
    accountNumber: "Secure Billing Provider",
    beneficiary: "NexoraX Digital Ltd (SC887095)",
    contactEmail: "support@nexoraxdigital.co.uk",
    voiceAnchor: "08000 622827"
  };

  // State Management for Current Invoice Form
  const [invoiceId, setInvoiceId] = useState<string>(`NEX-${new Date().getFullYear()}-${Math.floor(Math.random() * 9000 + 1000)}`);
  const [clientName, setClientName] = useState<string>("Vanguard Tech Solutions");
  const [clientEmail, setClientEmail] = useState<string>("operations@vanguardtech.io");
  const [grossAmount, setGrossAmount] = useState<number>(12500);
  const [description, setDescription] = useState<string>("NexoraX Neural Core Integration Node-09.2 Upgrade");
  
  // Interactive UI States
  const [isCopied, setIsCopied] = useState<boolean>(false);
  const [isBackingUp, setIsBackingUp] = useState<boolean>(false);
  const [backupLogs, setBackupLogs] = useState<string[]>([]);
  const [showBackupConsole, setShowBackupConsole] = useState<boolean>(false);
  const [qrPixelStyle, setQrPixelStyle] = useState<'cyber' | 'sharp'>('cyber');
  const [qrColorTheme, setQrColorTheme] = useState<'amber' | 'emerald' | 'cyan'>('amber');
  const [isGenerating, setIsGenerating] = useState<boolean>(false);

  // Math & Financial NexoraX Calculations
  const depositRatio = 0.05; // 5% Deposit Settlement
  const depositAmount = grossAmount * depositRatio;

  // Tiered Extraction Protocol (1% 3% Community Allocation, 10% Maintenance & Hosting Fee of the settlement amount)
  const NexoraXLevy = depositAmount * 0.01;
  const lifetimeTax = (depositAmount - NexoraXLevy) * 0.20;
  const netSettlement = depositAmount - NexoraXLevy - lifetimeTax;

  // Split-up ratios for Charity 1, 2 and 3 using exact 33.33% split of the 1% levy
  const charitySplit = NexoraXLevy / 3;
  const charityAllocation = {
    charity1: charitySplit, // Dunfermline
    charity2: charitySplit, // Global
    charity3: charitySplit  // UK
  };

  // State to hold the actively rendered matrix
  const [matrixGrid, setMatrixGrid] = useState<boolean[][]>([]);

  // Generate a procedural seed-based matrix for QR code styling
  const generateProceduralQRMatrix = () => {
    setIsGenerating(true);
    const size = 21; // 21x21 QR code grid
    const grid: boolean[][] = Array(size).fill(null).map(() => Array(size).fill(false));

    // Helper to draw standard 7x7 Finder Patterns at (r, c)
    const drawFinder = (row: number, col: number) => {
      for (let r = 0; r < 7; r++) {
        for (let c = 0; c < 7; c++) {
          const isBorder = r === 0 || r === 6 || c === 0 || c === 6;
          const isCenter = r >= 2 && r <= 4 && c >= 2 && c <= 4;
          grid[row + r][col + c] = isBorder || isCenter;
        }
      }
    };

    // Draw top-left, top-right, and bottom-left finders
    drawFinder(0, 0);
    drawFinder(0, size - 7);
    drawFinder(size - 7, 0);

    // Draw alignment tracks and timing patterns
    for (let i = 8; i < size - 8; i++) {
      grid[6][i] = i % 2 === 0;
      grid[i][6] = i % 2 === 0;
    }

    // Procedural pseudo-random data loading linked to state inputs
    const keyString = `${invoiceId}-${clientName}-${depositAmount}-${qrPixelStyle}-${qrColorTheme}`;
    let seedVal = 0;
    for (let i = 0; i < keyString.length; i++) {
      seedVal = (seedVal << 5) - seedVal + keyString.charCodeAt(i);
      seedVal |= 0;
    }

    // Dynamic procedural cell hash
    const getCellState = (r: number, c: number, seed: number) => {
      const idx = r * size + c;
      const combined = Math.sin(seed + idx) * 10000;
      return (combined - Math.floor(combined)) > 0.48;
    };

    const logoRadius = 3;
    const center = Math.floor(size / 2);

    for (let r = 0; r < size; r++) {
      for (let c = 0; c < size; c++) {
        // Skip reservation areas for patterns and logo safety zones
        const isTLFinder = r < 8 && c < 8;
        const isTRFinder = r < 8 && c >= size - 8;
        const isBLFinder = r >= size - 8 && c < 8;
        const isTiming = r === 6 || c === 6;
        const isCenterLogo = r >= center - logoRadius && r <= center + logoRadius && 
                             c >= center - logoRadius && c <= center + logoRadius;

        if (!isTLFinder && !isTRFinder && !isBLFinder && !isTiming && !isCenterLogo) {
          grid[r][c] = getCellState(r, c, seedVal);
        }
      }
    }

    setTimeout(() => {
      setMatrixGrid(grid);
      setIsGenerating(false);
    }, 250);
  };

  // Re-generate matrix when primary fields shift
  useEffect(() => {
    generateProceduralQRMatrix();
  }, [invoiceId, clientName, grossAmount, qrPixelStyle, qrColorTheme]);

  // Copy standard open banking SecurePayment payment link with node routing parameters
  const handleCopySecurePaymentLink = () => {
    const paymentUrl = `https://pay.SecurePayment.co/pay/${bankDetails.accountNumber}?invoice=${invoiceId}&amount=${depositAmount.toFixed(2)}&ref=${invoiceId}`;
    navigator.clipboard.writeText(paymentUrl);
    setIsCopied(true);
    setTimeout(() => setIsCopied(false), 2000);
  };

  // Back up simulated operational payload to Google Drive NexoraX Resource Library via CloudSync-02
  const handleGoogleDriveBackup = () => {
    if (isBackingUp) return;
    setIsBackingUp(true);
    setShowBackupConsole(true);
    
    const timestamp = new Date().toISOString();
    const mockBackupSteps = [
      `[${timestamp}] [CloudSync-02] Initializing Mirror Link Interface...`,
      `[${timestamp}] [SOURCE] Packing invoice payload state for local node: /var/www/nexorax_source`,
      `[${timestamp}] [ENCRYPTION] Locking payload using NexoraX SHA-256 neural key...`,
      `[${timestamp}] [TRANSMISSION] Synching payload containing { Invoice: ${invoiceId}, Client: ${clientName}, Deposit: £${depositAmount.toLocaleString()} }`,
      `[${timestamp}] [TARGET] Writing sector blocks to 'gdrive:Nexorax_Ecosystem_Backups/Source_Core_V2_LivingBrain/Invoices/${invoiceId}_Payload'`,
      `[${timestamp}] [VERIFICATION] Comparing remote hash value to guarantee zero-corruption transmission.`,
      `[${timestamp}] [INTEGRID] NexoraX extraction logging synced to Chronos Ledger sector...`,
      `[${timestamp}] [SUCCESS] BACKUP SECURED TO NexoraX Resource Library. AUTOMATED MIRRING CONFIRMED.`
    ];

    setBackupLogs([]);
    
    mockBackupSteps.forEach((step, idx) => {
      setTimeout(() => {
        setBackupLogs(prev => [...prev, step]);
        if (idx === mockBackupSteps.length - 1) {
          setIsBackingUp(false);
        }
      }, (idx + 1) * 350);
    });
  };

  // Helper theme classes mapping
  const activeColorThemeClasses = {
    amber: {
      accent: 'text-amber-500',
      borderClass: 'border-amber-500/30 hover:border-amber-500/60',
      bgLightClass: 'bg-amber-950/10',
      badgeClass: 'bg-amber-950/30 text-amber-400 border-amber-900/40',
      pixelClass: 'bg-amber-500 shadow-[0_0_8px_rgba(245,158,11,0.55)]'
    },
    emerald: {
      accent: 'text-emerald-400',
      borderClass: 'border-emerald-500/30 hover:border-emerald-500/60',
      bgLightClass: 'bg-emerald-950/10',
      badgeClass: 'bg-emerald-950/30 text-emerald-400 border-emerald-900/40',
      pixelClass: 'bg-emerald-400 shadow-[0_0_8px_rgba(52,211,153,0.55)]'
    },
    cyan: {
      accent: 'text-cyan-400',
      borderClass: 'border-cyan-500/30 hover:border-cyan-500/60',
      bgLightClass: 'bg-cyan-950/10',
      badgeClass: 'bg-cyan-950/30 text-cyan-400 border-cyan-900/40',
      pixelClass: 'bg-cyan-400 shadow-[0_0_8px_rgba(34,211,238,0.55)]'
    }
  };

  const activeColor = activeColorThemeClasses[qrColorTheme];

  return (
    <div className="w-full max-w-7xl mx-auto px-4 py-8 space-y-10 min-h-screen text-slate-100 font-mono" id="NexoraX-qr-generator-root">
      
      {/* Verified Religious Preamble Alignment */}
      <div className="text-center space-y-1.5 border-b border-amber-500/10 pb-6 max-w-2xl mx-auto" id="praise-preamble-banner">
        <p className="text-[10px] sm:text-[11px] font-black tracking-[0.18em] text-amber-500 uppercase leading-relaxed">
          In the name of Allah, the Greatest and Most Merciful. Praise be to Allah, Lord of the Worlds.
        </p>
        <p className="text-[8.5px] tracking-[0.25em] text-cyan-400 uppercase opacity-75">
          Nexorax NexoraX Neural Core Node // Office of the Director Lead Director
        </p>
      </div>

      {/* Main Feature Banner */}
      <div className="relative p-7 md:p-9 bg-slate-950 border border-amber-500/25 overflow-hidden rounded-3xl shadow-[0_0_40px_rgba(245,158,11,0.08)]" id="NexoraX-invoice-header">
        {/* Intricate digital matrix grid background */}
        <div className="absolute inset-0 opacity-[0.03] pointer-events-none">
          <div className="h-full w-full bg-[linear-gradient(to_right,#e2e8f0_1px,transparent_1px),linear-gradient(to_bottom,#e2e8f0_1px,transparent_1px)] bg-[size:24px_24px]" />
        </div>

        <div className="relative z-10 flex flex-col lg:flex-row items-stretch lg:items-center justify-between gap-6" id="header-split-grid">
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <span className="w-2.5 h-2.5 rounded-full bg-amber-500 animate-[ping_2.5s_infinite]" />
              <span className="text-[10px] tracking-[0.35em] font-black text-amber-400 uppercase leading-none">
                CORE PROTOCOL // NODE-07 QUANTUM BILLING
              </span>
            </div>
            <h1 className="text-slate-100 font-black text-2xl md:text-3xl uppercase tracking-wider leading-none">
              NexoraX QR Generator
            </h1>
            <p className="text-neutral-400 font-sans text-xs max-w-3xl leading-relaxed">
              Generate unique, SecurePayment-Bank-linked QR codes for client invoices to facilitate instant 
              <strong className="text-amber-500 font-bold"> 5% deposit settlement</strong>. Employs full multi-stage tax accounting logic, 
              incorporating the 1% 3% Community Allocation (charity breakdown) and 10% Maintenance & Hosting Fee (10% Maintenance & Hosting Fee) directly routed to raw ledger arrays.
            </p>
          </div>

          <div className="flex items-center gap-3.5 bg-[#03060f] p-4.5 border border-slate-900 rounded-2xl shrink-0 self-start lg:self-auto" id="anchor-bank-details">
            <Landmark className="w-7 h-7 text-amber-500" />
            <div className="text-[10px] leading-relaxed">
              <div className="text-neutral-500 font-bold uppercase tracking-widest text-[8px]">SecurePayment Routing Terminal</div>
              <div className="text-emerald-400 font-black text-xs">Sort: Verified Payment Configuration</div>
              <div className="text-emerald-400 font-black text-xs">Acc: Secure Billing Provider</div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Structural Working Sandbox */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-stretch" id="working-grid-row">
        
        {/* Left Hand: Config Panel (Col 7) */}
        <div className="lg:col-span-7 flex flex-col justify-between bg-[#02050b]/90 border border-slate-900 rounded-3xl p-6.5 space-y-6" id="invoice-configurator-panel">
          
          <div className="space-y-5">
            <div className="flex items-center gap-2 border-b border-slate-900 pb-3" id="config-title-element">
              <Sparkles className="w-4.5 h-4.5 text-amber-500 animate-pulse" />
              <h2 className="text-xs font-black uppercase tracking-widest text-slate-200">
                Invoice Schema Compiler
              </h2>
            </div>

            {/* Input fields */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" id="invoice-id-and-gross-grid">
              <div className="space-y-2">
                <label className="text-[10.5px] font-bold text-neutral-400 uppercase block">
                  1. Corporate Invoice ID:
                </label>
                <div className="relative">
                  <input 
                    type="text"
                    value={invoiceId}
                    onChange={(e) => setInvoiceId(e.target.value)}
                    className="w-full bg-black border border-slate-900 rounded-xl px-4 py-3 text-xs text-white focus:outline-none focus:border-amber-500 font-mono"
                    placeholder="e.g. NEX-2026-X"
                  />
                  <button 
                    type="button"
                    onClick={() => setInvoiceId(`NEX-${new Date().getFullYear()}-${Math.floor(Math.random() * 9000 + 1000)}`)}
                    className="absolute right-3.5 top-1/2 -translate-y-1/2 text-neutral-500 hover:text-white transition-colors cursor-pointer"
                    title="Generate unique code"
                  >
                    <RefreshCw className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-[10.5px] font-bold text-neutral-400 uppercase block">
                  2. Gross Invoice Value (£):
                </label>
                <input 
                  type="number"
                  value={grossAmount}
                  onChange={(e) => setGrossAmount(Math.max(0, Number(e.target.value)))}
                  className="w-full bg-black border border-slate-900 rounded-xl px-4 py-3 text-xs text-white focus:outline-none focus:border-amber-500"
                  placeholder="e.g. 5000"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" id="client-name-and-email-grid">
              <div className="space-y-2">
                <label className="text-[10.5px] font-bold text-neutral-400 uppercase block">
                  3. Client Enterprise Name:
                </label>
                <input 
                  type="text"
                  value={clientName}
                  onChange={(e) => setClientName(e.target.value)}
                  className="w-full bg-black border border-slate-900 rounded-xl px-4 py-3 text-xs text-white focus:outline-none focus:border-amber-500"
                  placeholder="e.g. Client Org"
                />
              </div>

              <div className="space-y-2">
                <label className="text-[10.5px] font-bold text-neutral-400 uppercase block">
                  4. Client Target Email:
                </label>
                <input 
                  type="email"
                  value={clientEmail}
                  onChange={(e) => setClientEmail(e.target.value)}
                  className="w-full bg-black border border-slate-900 rounded-xl px-4 py-3 text-xs text-white focus:outline-none focus:border-amber-500 font-sans"
                  placeholder="e.g. contact@client.com"
                />
              </div>
            </div>

            <div className="space-y-2" id="invoice-details-block">
              <label className="text-[10.5px] font-bold text-neutral-400 uppercase block">
                5. Provision Specification &amp; Description:
              </label>
              <textarea 
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={2}
                className="w-full bg-black border border-slate-900 rounded-xl px-4 py-3 text-xs text-white focus:outline-none focus:border-amber-500 resize-none font-sans"
                placeholder="Describe structural integration..."
              />
            </div>

            {/* Layout Aesthetics Configuration */}
            <div className="bg-black/50 border border-slate-900/60 p-4 rounded-2xl space-y-3" id="styling-configurator-card">
              <span className="text-[9.5px] font-black tracking-wider text-slate-400 uppercase block">
                QR Design Aesthetics
              </span>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <span className="text-[9px] text-zinc-500 block">Matrix Color Scheme:</span>
                  <div className="flex gap-2">
                    {[
                      { key: 'amber', label: 'Gold Aura' },
                      { key: 'emerald', label: 'Emerald' },
                      { key: 'cyan', label: 'Vanguard Cyan' }
                    ].map(theme => (
                      <button 
                        key={theme.key}
                        onClick={() => setQrColorTheme(theme.key as any)}
                        className={`flex-1 py-1.5 px-2 rounded-xl text-[9px] uppercase font-bold border transition-all cursor-pointer ${
                          qrColorTheme === theme.key 
                            ? 'bg-amber-500/10 border-amber-500 text-amber-400 font-extrabold' 
                            : 'border-slate-900 bg-neutral-950 text-neutral-500 hover:text-neutral-300'
                        }`}
                      >
                        {theme.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="space-y-2">
                  <span className="text-[9px] text-zinc-500 block">Pixel Module Formulation:</span>
                  <div className="flex bg-neutral-950 p-1 border border-slate-900 rounded-xl">
                    {[
                      { key: 'cyber', label: 'Cyber Dots' },
                      { key: 'sharp', label: 'Strict Sharp' }
                    ].map(style => (
                      <button 
                        key={style.key}
                        onClick={() => setQrPixelStyle(style.key as any)}
                        className={`flex-1 py-1.5 text-[9px] uppercase font-black tracking-wider rounded-lg transition-all cursor-pointer ${
                          qrPixelStyle === style.key 
                            ? 'bg-slate-900 text-white' 
                            : 'text-neutral-500 hover:text-neutral-300'
                        }`}
                      >
                        {style.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Verification Info Box */}
            <div className="flex items-start gap-3 p-3 bg-neutral-950 border border-slate-900 rounded-xl text-[10px] leading-relaxed text-zinc-400" id="access-to-work-notbox">
              <AlertCircle className="w-4.5 h-4.5 text-amber-500 shrink-0 mt-0.5" />
              <div className="space-y-1">
                <span className="text-slate-200 font-bold block uppercase tracking-wide text-[9.5px]">NexoraX Alignment Directive:</span>
                <span>
                  The 5% Deposit Settlement generates a rapid cash routing structure mapped to Secure Payment Provider 
                  Sort Code Verified Payment Configuration. Remainder of placement and agency fee operations adhere strictly to 
                  the NexoraX billing records scheme (NI: Secure Credential Stored Server-Side), direct billing models, and our Zero-Fund corporate guidelines.
                </span>
              </div>
            </div>

          </div>

          {/* Dynamic Actions */}
          <div className="pt-4 border-t border-slate-900 space-y-3" id="configurator-action-foot">
            <div className="flex flex-col sm:flex-row items-stretch gap-4">
              <button
                type="button"
                onClick={handleCopySecurePaymentLink}
                className="flex-1 py-3 px-4 bg-slate-900 hover:bg-slate-850 hover:scale-[1.01] border border-slate-800 text-white font-bold text-[11px] uppercase tracking-wider rounded-xl transition-all flex items-center justify-center gap-2 cursor-pointer shadow-lg"
              >
                {isCopied ? (
                  <>
                    <Check className="w-4 h-4 text-emerald-400" />
                    <span className="text-emerald-400">SecurePayment Link Copied</span>
                  </>
                ) : (
                  <>
                    <Copy className="w-4 h-4 text-slate-400" />
                    <span>Copy Native SecurePayment Booking Link</span>
                  </>
                )}
              </button>

              <button
                type="button"
                onClick={handleGoogleDriveBackup}
                disabled={isBackingUp}
                className="flex-1 py-3 px-4 bg-indigo-600 hover:bg-indigo-500 disabled:bg-indigo-950 disabled:text-indigo-700 text-white border-0 font-bold text-[11px] uppercase tracking-wider rounded-xl transition-all flex items-center justify-center gap-2 cursor-pointer shadow-lg shadow-indigo-950/20"
              >
                {isBackingUp ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin text-white" />
                    <span>Syncing NexoraX Resource Library...</span>
                  </>
                ) : (
                  <>
                    <HardDrive className="w-4 h-4 text-white" />
                    <span>Backup to Google Drive</span>
                  </>
                )}
              </button>
            </div>

            {/* Back up Diagnostic Console */}
            <AnimatePresence>
              {showBackupConsole && (
                <motion.div 
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="bg-black text-[9px] text-neutral-450 border border-slate-900 rounded-xl overflow-hidden shadow-inner p-3 font-mono space-y-1.5"
                  id="mirror-backup-console"
                >
                  <div className="flex justify-between items-center pb-1.5 border-b border-slate-900">
                    <span className="text-[#00ffcc] font-extrabold text-[8px] uppercase tracking-widest flex items-center gap-1">
                      <Database className="w-3.5 h-3.5" /> CloudSync-02 Cloud Mirror Transmission Status
                    </span>
                    <button 
                      onClick={() => setShowBackupConsole(false)}
                      className="text-neutral-500 hover:text-white uppercase text-[8px] border-none bg-transparent cursor-pointer"
                    >
                      [Dismiss]
                    </button>
                  </div>
                  <div className="h-44 overflow-y-auto space-y-1 pl-1">
                    {backupLogs.map((log, idx) => (
                      <div 
                        key={idx} 
                        className={`leading-relaxed break-all ${
                          log.includes("SUCCESS") 
                            ? 'text-emerald-400 font-extrabold' 
                            : log.includes("Error") || log.includes("[HALT]")
                              ? 'text-red-400'
                              : log.includes("[ENCRYPTION]")
                                ? 'text-cyan-300'
                                : 'text-neutral-400'
                        }`}
                      >
                        &gt; {log}
                      </div>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

        </div>

        {/* Right Hand: Visual QR Card Showcase & Ledger Calculations (Col 5) */}
        <div className="lg:col-span-5 flex flex-col justify-between gap-6" id="qr-ledger-showcase-panel">
          
          {/* Target Interactive Invoiced QR Card */}
          <div className="bg-slate-950 p-6 rounded-3xl border border-slate-900 shadow-xl flex flex-col items-center justify-center space-y-5" id="vector-qr-card-artifact">
            
            <div className="w-full flex justify-between items-center border-b border-slate-900/60 pb-3" id="panel-badge-group">
              <span className="text-[9.5px] font-black tracking-widest text-[#00ffcc] uppercase bg-[#011414] border border-[#00ffcc]/20 px-2.5 py-0.5 rounded-lg">
                Vector QR Matrix Print Target
              </span>
              <div className="flex items-center gap-1.5 bg-neutral-900 border border-slate-800 rounded-lg px-2 py-0.5 text-[8.5px] text-zinc-500">
                <ShieldCheck className="w-3.5 h-3.5 text-emerald-400" />
                <span>VERIFIED</span>
              </div>
            </div>

            {/* Main Visual QR Canvas block */}
            <div className="relative p-5 bg-black border-2 border-slate-900 rounded-2xl w-full max-w-[270px] aspect-square flex items-center justify-center shadow-inner" id="interactive-qr-container">
              {isGenerating ? (
                <div className="absolute inset-0 flex flex-col justify-center items-center bg-black/80 gap-2 rounded-2xl">
                  <RefreshCw className="w-6 h-6 text-amber-500 animate-spin" />
                  <span className="text-[9px] uppercase tracking-widest text-neutral-500 font-bold">Rendering Pixel Arrays</span>
                </div>
              ) : null}

              {/* Dynamic procedurally built SVG QR pixel map */}
              <div className="grid grid-cols-21 gap-[1.5px] bg-black select-all w-full h-full relative" id="vector-grid-elements">
                {matrixGrid.map((rowArr, rIdx) => 
                  rowArr.map((isActiveCell, cIdx) => (
                    <div 
                      key={`${rIdx}-${cIdx}`}
                      className={`w-full aspect-square transition-all duration-300 ${
                        isActiveCell 
                          ? activeColor.pixelClass
                          : 'bg-neutral-900/50'
                      } ${
                        qrPixelStyle === 'cyber' && isActiveCell ? 'rounded-[2.5px]' : ''
                      }`}
                    />
                  ))
                )}

                {/* Central High-Fidelity Branded Seal Badge */}
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-black p-[5.5px] rounded-xl border-2 border-slate-900 shadow-2xl flex items-center justify-center z-10 w-11 h-11">
                  <div className="w-full h-full bg-[#02050f] rounded flex flex-col justify-center items-center text-[7.5px] font-semibold border border-slate-800 leading-none">
                    <span className="text-amber-500 font-black tracking-tight uppercase">NEXO</span>
                    <span className="text-cyan-400 font-black tracking-tight uppercase text-[5.5px] mt-0.5">RAX</span>
                  </div>
                </div>
              </div>

            </div>

            {/* Badge specifications beneath the target code */}
            <div className="w-full text-center space-y-1.5" id="qr-target-footer-stats">
              <span className="text-xs text-white font-extrabold uppercase tracking-wide block">
                {invoiceId}
              </span>
              <div className="flex justify-center items-center gap-1.5 text-[9px] text-[#00ffcc] font-black tracking-wider uppercase bg-[#00171a] px-3 py-1 rounded border border-[#00ffcc]/15 max-w-max mx-auto">
                <HeartHandshake className="w-3.5 h-3.5" />
                <span>5% Deposit: £{depositAmount.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
              </div>
              <span className="text-[8px] text-zinc-500 uppercase block tracking-widest">
                Secure Payment Provider Deposit Settlement &bull; Secure Routing Node-07
              </span>
            </div>

          </div>

          {/* Tiered Extraction and Fiscal Protocol Ledger breakdown details */}
          <div className="bg-slate-950 p-5 rounded-3xl border border-slate-900 space-y-4" id="ledger-calculations-breakdown">
            <div className="flex items-center justify-between pb-2 border-b border-slate-900" id="calculation-badge-row">
              <span className="text-[10px] text-neutral-400 font-bold uppercase">
                Tiered Extraction Ledger Account
              </span>
              <span className="text-[8px] bg-indigo-950 border border-indigo-900/60 px-2 py-0.5 rounded text-indigo-400 uppercase tracking-widest">
                AUTOMATED TAX ENFORCEMENT
              </span>
            </div>

            {/* Calculations items list */}
            <div className="space-y-3 font-mono text-[11px]" id="ledger-distribution-rows">
              <div className="flex justify-between items-center py-0.5 text-zinc-400">
                <span>Invoice Gross Total:</span>
                <span className="text-slate-200 font-bold">£{grossAmount.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
              </div>

              <div className="flex justify-between items-center py-0.5 border-b border-dashed border-slate-900/80 pb-2 text-zinc-400">
                <span>Target Deposit (5%):</span>
                <span className="text-amber-500 font-extrabold">£{depositAmount.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
              </div>

              {/* 1% Levy detailed block */}
              <div className="space-y-1.5 bg-neutral-950/40 p-3 rounded-2xl border border-slate-900/70" id="levy-detailed-card">
                <div className="flex justify-between items-center font-bold text-cyan-400">
                  <span>1% 3% Community Allocation:</span>
                  <span>£{NexoraXLevy.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                </div>
                
                <div className="space-y-1 pl-3 text-[9px] text-zinc-500" id="charity-breakdown-allocations">
                  <div className="flex justify-between items-center">
                    <span>- Charity 1 (Dunfermline) [33.33%]:</span>
                    <span>£{charityAllocation.charity1.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span>- Charity 2 (Global) [33.33%]:</span>
                    <span>£{charityAllocation.charity2.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span>- Charity 3 (UK-wide) [33.33%]:</span>
                    <span>£{charityAllocation.charity3.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                  </div>
                </div>
              </div>

              {/* 10% Maintenance & Hosting Fee (10% Maintenance & Hosting Fee) */}
              <div className="flex justify-between items-center p-3 bg-[#0a0505] rounded-2xl border border-rose-950/20 text-rose-400 font-bold">
                <span>10% Maintenance & Hosting Fee (10% Maintenance & Hosting Fee):</span>
                <span>£{lifetimeTax.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
              </div>

              {/* Net Settlement to Secure Payment Provider Account */}
              <div className="flex justify-between items-center p-3 bg-[#050a06] rounded-2xl border border-emerald-950/20 text-emerald-400 font-black">
                <span>Net Transfer Routing to SecurePayment:</span>
                <span>£{netSettlement.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
              </div>
            </div>

            <div className="pt-2 border-t border-slate-900 text-[8.5px] leading-relaxed text-zinc-500 uppercase flex items-center justify-between" id="ledger-ledger-state-confirm">
              <span>Ledger State Verified IP-Protected</span>
              <span>Sorted &bull; SecurePayment Node-07 Verified</span>
            </div>

          </div>

        </div>

      </div>

      {/* Auxiliary Help Desk Panel */}
      <div className="p-6 bg-[#01040a] rounded-3xl border border-slate-900 flex flex-col md:flex-row items-stretch md:items-center justify-between gap-6" id="NexoraX-help-desk-banner">
        <div className="space-y-1.5 text-left">
          <div className="flex items-center gap-1.5 text-amber-500 font-black tracking-wide uppercase text-xs">
            <Key className="w-4.5 h-4.5 text-amber-500 animate-pulse" />
            NexoraX Admin Voice Pipeline &amp; Hotline Contacts
          </div>
          <p className="text-zinc-500 text-[10.5px] leading-relaxed max-w-4xl uppercase">
            If a client attempts to negotiate settled values or levy routing parameters outside standard open banking 
            regulations, terminate the transaction immediately. Route administrative inquiries to:
            <strong className="text-slate-300"> {bankDetails.contactEmail} </strong> 
            or invoke the official Voice Pipeline on freephone:
            <strong className="text-slate-300"> {bankDetails.voiceAnchor} </strong>.
          </p>
        </div>

        <div className="shrink-0 flex items-center gap-2 text-indigo-400 text-[10.5px] font-bold uppercase bg-indigo-950/20 border border-indigo-900/40 px-4 py-2.5 rounded-xl" id="guardian-version-badge">
          <Lock className="w-4 h-4 text-indigo-400" />
          <span>CloudSync-02 Active Armor (v2.7.4)</span>
        </div>
      </div>

    </div>
  );
};
